import { useEffect, useMemo, useState } from "react";
import { useApiDispatch, useApiStore } from "./global";
import { FlowType, GetFlow, GetFlowList } from "server-sdk/src/types/workflow";
import { ListingStatus, matchPropertyQuery } from "server-sdk/src/types";
import { usePortfolioRefresher } from "./property";
import { useRouter } from "../router";
import { LISTING_BUFFER_DAYS } from "server-sdk/src/constants";

export const useFlow = (id?: number, activityId?: number) => {
  const [flow, setFlow] = useState<any>(undefined);
  const [flowActivity, setFlowActivity] = useState<any | undefined>(undefined);
  const [flowList, setFlowList] = useState<GetFlowList | undefined>(undefined);
  const [lastSeenUpdated, setLastSeenUpdated] = useState<GetFlow | undefined>(undefined);
  const [initialized, setInitialized] = useState<boolean>(false);
  const router = useRouter();

  const workflowMenuItems = [
    {
      title: 'Lease property',
      onClick: () => {
        router.push(`/owner/workflow/start/listing-setup`);
      },
    },
    {
      title: 'Add existing tenant',
      onClick: () => {
        router.push(`/owner/workflow/start/existing-renter`);
      },
    },
    // {
    //   title: 'Request work order',
    //   onClick: () => {},
    // },
  ];
  const apiDispatch = useApiDispatch();

  const initializeComment = (setOnThinking) => {
    if (initialized) {
      return;
    }
    setInitialized(true);
    apiDispatch(async (call, api) => {
      setOnThinking(true);
      await call(api.flows.initializationComment, {id: id});
      refreshFlow(() => setOnThinking(false));
      return [true]
    })
  }

  const refreshFlow = (callback?: (flow: any) => void) => {
    if (id) {
      apiDispatch(async (call, api) => {
        const res = await call(api.flows.get, {
          id: id
        });
        if (!res) {
          return [false];
        }
        setFlow(res.flow);
        if(callback !== undefined){
          callback(res.flow);
        }
        return [true];
      });
    }
  };

  const refreshFlowActivity = () => {
    if (activityId) {
      apiDispatch(async (call, api) => {
        const res = await call(api.flows.getActivity, {
          activity: {
            id: activityId,
            type: "comment",
            content: "",
            creation_time: 0,
          },
        });
        if (!res) {
          return [false, "failed to retreive flow activity"];
        }
        setFlowActivity(res.activity);

        return [true];
      });
    }
  };

  const refreshFlowList = () => {
    apiDispatch(async (call, api) => {
      const res = await call(api.flows.list);
      if (!res) {
        return [false];
      }

      setFlowList(res);
      return [true];
    });
  };

  const updateLastSeen = () => {
    apiDispatch(async (call, api) => {
      if (flow?.id) {
        const res = await call(api.flows.updateLastSeen, {
          id: flow.id,
        });
        if (!res) {
          return [false];
        }
        setLastSeenUpdated(res);
      }
      return [true];
    });
  };

  const addComment = (
    comment: string,
    callback: (commentId: number) => void,
    setUploading?: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    apiDispatch(async (call, api) => {
      if (setUploading !== undefined) {
        setUploading(true);
      }
      let newComment = await call(api.flows.uploadComment, {
        flow_id: id,
        content: comment,
      });
      if (!newComment) {
        if (setUploading !== undefined) {
          setUploading(false);
        }
        return [false, "Failed to upload note, please try again later"];
      }
      updateLastSeen();
      await callback(newComment.id);
      if (setUploading !== undefined) {
        setUploading(false);
      }
      return [true];
    });
  };

  useEffect(() => {
    refreshFlow();
    refreshFlowList();
    refreshFlowActivity();
  }, []);

  return {
    flow,
    flowList,
    flowActivity,
    refreshFlow,
    refreshFlowList,
    refreshFlowActivity,
    addComment,
    updateLastSeen,
    lastSeenUpdated,
    initializeComment,
    workflowMenuItems,
  };
};

export const useFlowRedirect = () => {
  const apiDispatch = useApiDispatch();

  const handleListingSetupAction = (
    isMobile: boolean,
    router: any,
    type: string,
    propertyId: number | undefined,
    flowActivityId: number,
    metadata?: any
  ) => {
    if (type === "property-info") {
      if (propertyId) {
        isMobile
          ? router("PropertyForms", {
              type,
              propertyId: propertyId,
              flowActivityId: flowActivityId,
            })
          : router(
              `/owner/workflow/forms/${flowActivityId}/listing-setup/${type}/${propertyId}`
            );
      } else {
        isMobile
          ? console.log("INITIAL PROPERTY CREATION FORM")
          : router(
              `/owner/workflow/forms/${flowActivityId}/listing-setup/${type}/`
            );
      }
    } else if (
      type === "activate-listing" ||
      type === "setup-touring"
    ) {
      isMobile
        ? router("PropertyForms", {
            type,
            propertyId: propertyId,
            flowActivityId: flowActivityId,
          })
        : router(
            `/owner/workflow/forms/${flowActivityId}/listing-setup/${type}/${propertyId}`
          );
    }
    else if (type === 'lock-in-transit'){
      isMobile ?
      undefined :
      router(`http://localhost:8000/owner/shipment?id=${metadata.shipmentId}`)
    }
    else if (
      type === "setup-lock"
    ){
      isMobile ?
      router("LockManagement", {
        propertyId: propertyId,
      }) :
      router(`/owner/portfolio/property/${propertyId}/property-access`)
    } else if (type === "select-applicant") {
      router(`/owner/portfolio/property/${propertyId}/listing`)
    }
    else if (type === "select-applicant") {
      isMobile
      ? router("LeasingActivity", {
          propertyId: propertyId,
        })
      : router(`/owner/portfolio/property/${propertyId}/listing`);
    }
  };

  const handleLeasingReportAction = (
    isMobile: boolean,
    router: any,
    type: string,
    propertyId: number | undefined,
    flowActivityId: number,
    metadata?: any
  ) => {
    if (type === "select-applicant") {
      isMobile
        ? router("LeasingActivity", {
            propertyId: propertyId,
          })
        : router(`/owner/portfolio/property/${propertyId}/listing`);
    }
  };

  const handleMoveInAction = (
    isMobile: boolean,
    router: any,
    type: string,
    flowActivityId: number,
    metadata?: any
  ) => {
    if (type === "lease-review" || type === "request-move-in-details") {
      isMobile
        ? router("MoveInForms", {
            type,
            applicationId: metadata.applicationId,
            flowActivityId: flowActivityId,
          })
        : router(
            `/owner/workflow/forms/${flowActivityId}/move-in/${type}/${metadata.applicationId}`
          );
    } else if (type === "lease-generation") {
      if (isMobile && !metadata.guid) {
        apiDispatch(async (call, api) => {
          const leaseInfo = await call(api.application.leaseReview.addGuid, {
            id: metadata.applicationId,
          });
          await call(api.flows.updateMetadata, {
            id: flowActivityId,
            metadata: {
              ...metadata,
              guid: leaseInfo.landlord_guid,
            },
          });
          router("LeaseGenerationWebView", {
            type,
            applicationId: metadata.applicationId,
            guid: leaseInfo.landlord_guid,
            flowActivityId: flowActivityId,
          });
          return [true];
        });
      } else {
        isMobile
          ? router("LeaseGenerationWebView", {
              type,
              applicationId: metadata.applicationId,
              guid: metadata.guid,
              flowActivityId: flowActivityId,
            })
          : router(
              `/owner/workflow/forms/${flowActivityId}/move-in/${type}/${metadata.applicationId}`
            );
      }
    }
    else if (type === "add-bank-information") {
      isMobile
        ? router("MoveInForms", {
            type,
            flowActivityId: flowActivityId,
          })
        : router(
            `/owner/workflow/forms/${flowActivityId}/move-in/${type}`
          );
    }
  }

  const handleExistingRenterAction = (
    isMobile: boolean,
    router: any,
    type: string,
    propertyId: number | undefined,
    flowActivityId: number
  ) => {
    if (
      type === "rent-terms" ||
      type === "lease-upload" ||
      type === "renter-invite" ||
      type === "add-bank-information"
    ) {
      isMobile
        ? router("ExistingRenterForms", {
            type,
            propertyId,
            flowActivityId,
          })
        : router(
            `/owner/workflow/forms/${flowActivityId}/existing-renter/${type}/${propertyId}`
          );
    }
  };

  const handleMaintenanceAction = (
    isMobile: boolean,
    router: any,
    type: string,
    propertyId: number | undefined,
    flowActivityId: number,
    metadata?: any
  ) => {
    if (type === "review-request") {
      isMobile
        ? router("LandlordMaintReqApprovalForm", {
            flowActivityId,
            maintenanceId: metadata.maintenanceId,
          })
        : router(`/maintenance/maintenance-request-approval?maintenanceId=${metadata.maintenanceId}&flowActivityId=${flowActivityId}`);
    }
    if (type === "home-warranty-confirmation") {
      isMobile
        ? router("MaintenanceHomeWarrantyForm", {
            flowActivityId,
            maintenanceId: metadata.maintenanceId,
          })
        : router(`/maintenance/home-warranty-confirmation?maintenanceId=${metadata.maintenanceId}&flowActivityId=${flowActivityId}`);
    }
    if (type === "choose-quote") {
      isMobile
        ? router("ChooseMaintenanceQuote", {
            flowActivityId,
            maintenanceId: metadata.maintenanceId,
          })
        : router(`/maintenance/choose-maintenance-quote?maintenanceId=${metadata.maintenanceId}&flowActivityId=${flowActivityId}`);
    }
    if (type === "submit-own-vendor") {
      isMobile
        ? router("SubmitOwnVendorForm", {
            flowActivityId,
            maintenanceId: metadata.maintenanceId,
          })
        : router(`/maintenance/submit-own-vendor?maintenanceId=${metadata.maintenanceId}&flowActivityId=${flowActivityId}`);
    }
    if (type === "close-request") {
      apiDispatch(async (call, api) => {
        const res = await call(api.flows.complete, {
          id: flowActivityId,
          metadata,
        });
        if (!res) {
          return [false, "Failed to complete flow, please try again"];
        }
        isMobile
        ? router("Root")
        : router(`/owner`);
        return [true];
      });
    }
    if (type === "review-completed-trip") {
      isMobile
        ? router("ReviewCompletedTrip", {
            flowActivityId,
            maintenanceId: metadata.maintenanceId,
            maintenanceTripId: metadata.maintenanceTripId,
          })
        : router(
            `/maintenance/review-completed-trip?maintenanceId=${metadata.maintenanceId}&maintenanceTripId=${metadata.maintenanceTripId}&flowActivityId=${flowActivityId}`
          );
    }
    if (type === "request-feedback") {
      isMobile
        ? router("MaintenanceOwnerFeedback", {
            flowActivityId,
            maintenanceId: metadata.maintenanceId,
          })
        : router(
            `/maintenance/owner-feedback?maintenanceId=${metadata.maintenanceId}&flowActivityId=${flowActivityId}`
          );
    }
    if (type === "view-completed-request") {
      isMobile
        ? router("ViewCompletedRequest", {
            maintenanceId: metadata.maintenanceId,
          })
        : router(
            `/maintenance/view-completed-request?maintenanceId=${metadata.maintenanceId}`
          );
    }
  };
  const handleLockInstallAction = (
    isMobile: boolean,
    router: any,
    type: string,
    propertyId: number | undefined,
    flowActivityId: number
  ) => {
      isMobile
        ? router("LockInstallForms", {
            type,
            propertyId: propertyId,
            flowActivityId: flowActivityId,
          })
        : router(
            `/owner/workflow/forms/${flowActivityId}/lock-install/${type}/${propertyId}`
          );
  }

  const handleLeasingAddendumAction = (
    isMobile: boolean,
    router: any,
    type: string,
    flowActivityId: number,
    metadata?: any
  ) => {
    if (type === "landlord-review-request" || type === 'create-lease-addendum'){
      isMobile
        ? router("LeasingAddendumForms", {
            type,
            leaseAddendumInfoId: metadata.lease_addendum_info_id,
            flowActivityId: flowActivityId,
          })
        : router(
            `/owner/workflow/forms/${flowActivityId}/leasing-addendum/${type}/${metadata.lease_addendum_info_id}`
          );
    }
  };

  const handlePropertyNavigate = (
    isMobile: boolean,
    router: any,
    type: string,
    propertyId: number | undefined,
    flowActivityId: number,
    flowId: number
  ) => {
    isMobile
      ? router("WorkflowComponent", {
          id: flowId,
        })
      : router(`/owner/workflow/${flowId}}`);
  };
  
  const handleMoveOutAction = (
    isMobile: boolean,
    router: any,
    type: string,
    propertyId: number | undefined,
    flowActivityId: number,
    linkedFlowId?: number,
    metadata?: any
  ) => {
    if (linkedFlowId) {
      isMobile
      ? (
          router("Root"),
          router("WorkflowComponent", {
            id: linkedFlowId,
          })
        )
      : router(`/owner/workflow/${linkedFlowId}`);
    } else {
      if (type === "complete-move-out-form") {
        isMobile
          ? router("MoveOutForm", {
              flowActivityId,
            })
          : router(`/moveout/move-out-form?flowActivityId=${flowActivityId}`);
      }
    }
  };

  return {
    handleListingSetupAction,
    handleMoveInAction,
    handleExistingRenterAction,
    handlePropertyNavigate,
    handleLeasingReportAction,
    handleMaintenanceAction,
    handleLeasingAddendumAction,
    handleLockInstallAction,
    handleMoveOutAction
  };
};

export const useFlowStart = (type: FlowType, mobile: boolean, router: any) => {
  const [properties, refreshProperties] = useApiStore((api) => api.property.list);
  const [started, startFlow] = useApiStore((api) => api.flows.start);
  const [search, setSearch] = useState<string>("");
  const [me, fetchMe] = useApiStore((api) => api.users.v2.me);

  const {
    refreshFlowList,
  } = useFlow();

  const refresh = usePortfolioRefresher();

  useEffect(() => {
    refreshProperties([]);
    fetchMe({});

    if (started) {
      console.warn('refreshing flow list after starting')
      refreshFlowList();
      refresh.properties();
    }
  }, [started]);

  const title = useMemo(() => {
    switch (type) {
      case 'listing-setup':
        return 'Start Leasing';
      case 'existing-renter':
        return 'Existing Renter Setup';
      case 'leasing-addendum':
        return 'Leasing Addendum Request';
      default:
        return 'Starting a new workflow';
    }
  }, [type]);

  let filteredProperties = useMemo(() => properties?.filter((property) => matchPropertyQuery(property, search)), [properties, search])
  filteredProperties?.map((property) => {
    const expiresSoon = (property.lease_days_remaining ?? 0) < LISTING_BUFFER_DAYS;
    switch (type) {
      case 'leasing-addendum':
        property.disabled = !property.rented
        property.disabled_reason = "No existing rental"
        break;
      case 'listing-setup':
        property.disabled = !expiresSoon || (property.listing_status ?? 0) > ListingStatus.Off
        property.disabled_reason = !expiresSoon ? "Property is already rented" : "Property is already listed"
        break;
      case 'existing-renter':
        property.disabled = property.rented;
        property.disabled_reason = "Property is already rented";
        break;
    }
  })

  filteredProperties = [
    ...filteredProperties?.filter(x => !x.disabled) ?? [],
    ...filteredProperties?.filter(x => x.disabled) ?? []
  ];

  const start = (propertyId?: number) => {
    switch (type) {
      case 'listing-setup':
        startFlow({
          params: {
            type: 'listing-setup',
            args: {
              propertyId: propertyId ?? 0,
              userId: me?.id ?? 0,
            }
          }
        });
        return;
      case 'existing-renter':
        startFlow({
          params: {
            type: 'existing-renter',
            args: {
              propertyId: propertyId ?? 0,
              userId: me?.id ?? 0,
            },
          },
        });
        return;
      case 'leasing-addendum':
        mobile
          ? router.navigate("OwnerAddendumRequestForm", {
              propertyId: propertyId,
            })
          : router.push(`/owner/addendum-request/${propertyId}`);
        return;
      default:
        console.warn('unknown flow type', type);
        return;
    }
  };

  return {
    properties: filteredProperties,
    started,
    search,
    setSearch,
    start,
    title,
  };
}
